
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { AirportLeaderboardStats } from "@/types/airport";

export function useAirportLeaderboard() {
  const [showWorst, setShowWorst] = useState(false);

  const { data: topAirportStats = [], isLoading: isLoadingTop } = useQuery({
    queryKey: ['airportLeaderboardTop'],
    queryFn: async () => {
      // Fetch all reviews to calculate accurate statistics
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('airport_name, airport_location, film_friendly_score, hand_check_success');
      
      if (reviewsError) throw reviewsError;
      
      // Group reviews by airport and calculate stats
      const airportsMap = new Map();
      
      reviews?.forEach(review => {
        if (!airportsMap.has(review.airport_name)) {
          airportsMap.set(review.airport_name, {
            airport_name: review.airport_name,
            airport_location: review.airport_location,
            scores: [],
            hand_checks: [],
            total_reviews: 0
          });
        }
        
        const airport = airportsMap.get(review.airport_name);
        airport.scores.push(review.film_friendly_score);
        airport.hand_checks.push(review.hand_check_success);
        airport.total_reviews += 1;
      });
      
      // Convert map to array and calculate final stats
      const airportsArray = Array.from(airportsMap.values()).map(airport => {
        const average_score = airport.scores.length > 0 
          ? airport.scores.reduce((sum: number, score: number) => sum + score, 0) / airport.scores.length 
          : 0;
          
        const hand_check_success_rate = airport.hand_checks.length > 0
          ? (airport.hand_checks.filter(Boolean).length / airport.hand_checks.length) * 100
          : 0;
          
        return {
          airport_id: airport.airport_name, // Using name as ID for consistency
          airport_name: airport.airport_name,
          airport_location: airport.airport_location,
          average_score: parseFloat(average_score.toFixed(1)),
          total_reviews: airport.total_reviews,
          hand_check_success_rate: parseFloat(hand_check_success_rate.toFixed(1))
        };
      });
      
      // Sort by hand_check_success_rate in descending order first, then by average_score
      return airportsArray
        .sort((a, b) => {
          // First sort by hand check success rate
          const rateDiff = b.hand_check_success_rate - a.hand_check_success_rate;
          if (Math.abs(rateDiff) > 0.01) return rateDiff;
          
          // Then by average score
          const scoreDiff = b.average_score - a.average_score;
          if (Math.abs(scoreDiff) > 0.01) return scoreDiff;
          
          // Finally by number of reviews as a tiebreaker
          return b.total_reviews - a.total_reviews;
        })
        .slice(0, 10) as AirportLeaderboardStats[];
    }
  });

  const { data: worstAirportStats = [], isLoading: isLoadingWorst } = useQuery({
    queryKey: ['airportLeaderboardWorst'],
    queryFn: async () => {
      // Fetch all reviews to calculate accurate statistics
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('airport_name, airport_location, film_friendly_score, hand_check_success');
      
      if (reviewsError) throw reviewsError;
      
      // Group reviews by airport and calculate stats
      const airportsMap = new Map();
      
      reviews?.forEach(review => {
        if (!airportsMap.has(review.airport_name)) {
          airportsMap.set(review.airport_name, {
            airport_name: review.airport_name,
            airport_location: review.airport_location,
            scores: [],
            hand_checks: [],
            total_reviews: 0
          });
        }
        
        const airport = airportsMap.get(review.airport_name);
        airport.scores.push(review.film_friendly_score);
        airport.hand_checks.push(review.hand_check_success);
        airport.total_reviews += 1;
      });
      
      // Convert map to array and calculate final stats
      const airportsArray = Array.from(airportsMap.values()).map(airport => {
        const average_score = airport.scores.length > 0 
          ? airport.scores.reduce((sum: number, score: number) => sum + score, 0) / airport.scores.length 
          : 0;
          
        const hand_check_success_rate = airport.hand_checks.length > 0
          ? (airport.hand_checks.filter(Boolean).length / airport.hand_checks.length) * 100
          : 0;
          
        return {
          airport_id: airport.airport_name, // Using name as ID for consistency
          airport_name: airport.airport_name,
          airport_location: airport.airport_location,
          average_score: parseFloat(average_score.toFixed(1)),
          total_reviews: airport.total_reviews,
          hand_check_success_rate: parseFloat(hand_check_success_rate.toFixed(1))
        };
      });
      
      // Sort by hand_check_success_rate in ascending order for worst airports, then by average_score
      return airportsArray
        .sort((a, b) => {
          // First sort by hand check success rate (ascending for worst)
          const rateDiff = a.hand_check_success_rate - b.hand_check_success_rate;
          if (Math.abs(rateDiff) > 0.01) return rateDiff;
          
          // Then by average score (ascending for worst)
          const scoreDiff = a.average_score - b.average_score;
          if (Math.abs(scoreDiff) > 0.01) return scoreDiff;
          
          // Finally by number of reviews as a tiebreaker (more reviews = worse ranking for worst list)
          return b.total_reviews - a.total_reviews;
        })
        .slice(0, 10) as AirportLeaderboardStats[];
    },
    enabled: showWorst
  });

  const airportStats = showWorst ? worstAirportStats : topAirportStats;
  const isLoading = showWorst ? isLoadingWorst : isLoadingTop;

  return {
    airportStats,
    isLoading,
    showWorst,
    setShowWorst
  };
}
